import { useEffect, useState } from 'react';

/**
 * Fetches an image URL and converts it to a base64 data URL.
 * Accepts optional RequestInit (e.g. auth headers) for API proxy requests.
 */
export async function fetchImageAsDataUrl(
  url: string | null | undefined,
  init?: RequestInit
): Promise<string | null> {
  if (!url) return null;
  if (url.startsWith('data:')) return url;
  try {
    const response = await fetch(url, init);
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    const blob = await response.blob();
    return await new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  } catch {
    return null;
  }
}

/**
 * Hook that fetches an image URL and converts it to a base64 data URL.
 * Accepts an optional async header provider for authenticated API requests.
 */
export function useImageToDataUrl(
  url: string | null | undefined,
  getHeaders?: () => Promise<HeadersInit>
): { dataUrl: string | null; isLoading: boolean } {
  const [dataUrl, setDataUrl] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!url) {
      setDataUrl(null);
      setIsLoading(false);
      return;
    }

    if (url.startsWith('data:')) {
      setDataUrl(url);
      setIsLoading(false);
      return;
    }

    let cancelled = false;
    setIsLoading(true);

    (async () => {
      try {
        const headers = getHeaders ? await getHeaders() : undefined;
        const result = await fetchImageAsDataUrl(url, headers ? { headers } : undefined);
        if (!cancelled) {
          setDataUrl(result);
          setIsLoading(false);
        }
      } catch {
        if (!cancelled) {
          setDataUrl(null);
          setIsLoading(false);
        }
      }
    })();

    return () => { cancelled = true; };
  }, [url, getHeaders]);

  return { dataUrl, isLoading };
}
